// AddIntegrationDialog.tsx
import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogClose,
} from "components/ui/dialog";
import { useIntegrations, useConnectApp } from "core/features/integration/hooks";
import { EnumIntegrationStatus } from "core";
import {
  Search,
  X,
  Zap,
  Users,
  Database,
  Activity,
  CheckCircle,
  ArrowRight,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { Skeleton } from "components/ui/skeleton";

type Props = {
  open: boolean;
  setOpen: (v: boolean) => void;
  getPlatformIcon?: (type?: string) => React.ReactNode;
  handleAddIntegration?: (id: number) => void;
};

/** Accent palette cycling for integration cards */
const ACCENT_PALETTE = [
  {
    bar: "from-blue-500 to-indigo-500",
    iconBg: "bg-blue-50 dark:bg-blue-900/20",
    iconColor: "text-blue-600 dark:text-blue-400",
    badge: "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 border-blue-100 dark:border-blue-800/30",
  },
  {
    bar: "from-emerald-400 to-teal-500",
    iconBg: "bg-emerald-50 dark:bg-emerald-900/20",
    iconColor: "text-emerald-600 dark:text-emerald-400",
    badge: "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-emerald-100 dark:border-emerald-800/30",
  },
  {
    bar: "from-violet-500 to-purple-500",
    iconBg: "bg-violet-50 dark:bg-violet-900/20",
    iconColor: "text-violet-600 dark:text-violet-400",
    badge: "bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-400 border-violet-100 dark:border-violet-800/30",
  },
  {
    bar: "from-amber-400 to-orange-500",
    iconBg: "bg-amber-50 dark:bg-amber-900/20",
    iconColor: "text-amber-600 dark:text-amber-400",
    badge: "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-100 dark:border-amber-800/30",
  },
];

/** Fallback icon based on integration type */
function PlatformIcon({ type, className }: { type?: string; className?: string }) {
  if (!type) return <Zap className={className ?? "w-4 h-4"} />;
  const t = type.toLowerCase();
  if (t.includes("crm") || t.includes("sales") || t.includes("hubspot") || t.includes("pipe") || t.includes("zoho"))
    return <Users className={className ?? "w-4 h-4"} />;
  if (t.includes("ecommerce") || t.includes("shop") || t.includes("woo") || t.includes("magento") || t.includes("big"))
    return <Database className={className ?? "w-4 h-4"} />;
  if (t.includes("social") || t.includes("twitter") || t.includes("linkedin") || t.includes("facebook"))
    return <Activity className={className ?? "w-4 h-4"} />;
  return <Zap className={className ?? "w-4 h-4"} />;
}

export default function AddIntegration({
  open,
  setOpen,
  getPlatformIcon: _getPlatformIcon,
  handleAddIntegration,
}: Props) {
  const { data, isLoading, error } = useIntegrations();
  const { mutate: connect, isPending } = useConnectApp();
  const [search, setSearch] = React.useState("");
  const [connecting, setConnecting] = React.useState<string | null>(null);

  const integrations =
    data?.filter(
      (i: any) =>
        (i.connectionStatus ?? i.status) !== EnumIntegrationStatus.Connected
    ) ?? [];

  const filtered = search.trim()
    ? integrations.filter((i: any) =>
        i.name?.toLowerCase().includes(search.toLowerCase()) ||
        (i.type ?? "").toLowerCase().includes(search.toLowerCase())
      )
    : integrations;

  const handleConnect = (integration: any) => {
    setConnecting(integration.id);
    if (connect) {
      connect(integration.name, {
        onSettled: () => setConnecting(null),
      });
    } else if (handleAddIntegration) {
      handleAddIntegration(integration.id);
      setConnecting(null);
    }
  };

  // Reset search when dialog closes
  React.useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent aria-labelledby="add-integration-title"
        align="top"
        showClose={false}
        padding={false}
        className="w-[92vw] sm:w-[min(580px,92vw)] md:w-[min(640px,92vw)]"
      >
        {/* ── Header ── */}
        <div className="relative px-6 pt-6 pb-4 border-b border-line dark:border-dark-700 flex-shrink-0">
          {/* Accent bar at very top */}
          <div className="absolute top-0 inset-x-0 h-[2px] rounded-t-xl bg-gradient-to-r from-blue-500 via-indigo-500 to-violet-500" />

          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              {/* Icon block */}
              <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-blue-600 dark:text-blue-400" strokeWidth={2} />
              </div>
              <div>
                <h2 id="add-integration-title" className="text-title font-bold text-content dark:text-white leading-tight">
                  Add Integration
                </h2>
                <p className="text-[13px] font-medium text-content-muted dark:text-gray-400 mt-0.5">
                  Connect your platform with external services
                </p>
              </div>
            </div>
            {/* Close button */}
            <DialogClose asChild>
              <button
                onClick={() => setOpen(false)}
                className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg
                  text-content-subtle hover:text-content-secondary dark:text-gray-500 dark:hover:text-gray-300
                  hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors duration-150"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </DialogClose>
          </div>

          {/* Search bar */}
          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-subtle dark:text-gray-500 pointer-events-none" />
            <input
              type="text"
              placeholder="Search integrations…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 text-[13px] font-medium
                bg-gray-50 dark:bg-dark-700
                border border-line dark:border-dark-600
                rounded-xl text-gray-800 dark:text-gray-200
                placeholder-gray-400 dark:placeholder-gray-500
                focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 dark:focus:border-blue-500
                transition-colors duration-150"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-content-subtle hover:text-content-secondary dark:hover:text-gray-300"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2"
          style={{ scrollbarWidth: "thin" }}
        >
          {isLoading ? (
            /* Skeleton loading — matches card shape exactly */
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="relative overflow-hidden flex items-center gap-3 p-3.5 rounded-xl
                    border border-line dark:border-dark-700 dark:bg-dark-700/40"
                >
                  <div className="absolute top-0 inset-x-0 h-[2px] loading-shimmer" />
                  <Skeleton className="w-9 h-9 rounded-lg flex-shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton variant="text" className="!w-32" />
                    <Skeleton variant="text" className="!w-20" />
                  </div>
                  <Skeleton className="w-20 h-7 rounded-lg" />
                </div>
              ))}
            </div>
          ) : error ? (
            /* Error state */
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="w-12 h-12 rounded-xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-3">
                <AlertCircle className="w-6 h-6 text-red-500 dark:text-red-400" />
              </div>
              <p className="text-[13px] font-semibold text-gray-800 dark:text-gray-200 mb-1">
                Failed to load integrations
              </p>
              <p className="text-[12px] font-medium text-content-muted dark:text-gray-400">
                Please try again or contact support.
              </p>
            </div>
          ) : filtered.length === 0 ? (
            /* Empty state */
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mb-3">
                <CheckCircle className="w-6 h-6 text-blue-500 dark:text-blue-400" />
              </div>
              <p className="text-[13px] font-semibold text-gray-800 dark:text-gray-200 mb-1">
                {search ? "No results found" : "All integrations connected! 🎉"}
              </p>
              <p className="text-[12px] font-medium text-content-muted dark:text-gray-400">
                {search ? `No integrations match "${search}"` : "Your platform is fully connected."}
              </p>
            </div>
          ) : (
            /* Integration list */
            filtered.map((integration: any, idx: number) => {
              const accent = ACCENT_PALETTE[idx % ACCENT_PALETTE.length];
              const typeLabel = integration.type ?? integration.name;
              const isThisConnecting = connecting === integration.id && isPending;

              return (
                <button
                  key={integration.id}
                  onClick={() => handleConnect(integration)}
                  disabled={isPending}
                  className="relative overflow-hidden group w-full text-left
                    flex items-center gap-3 p-3.5 rounded-xl
                    border border-line dark:border-dark-700
                    dark:bg-dark-700/30 hover:dark:bg-dark-700/60
                    hover:bg-gray-50 dark:hover:bg-dark-700 hover:border-line dark:hover:border-dark-600
                    hover:shadow-sm
                    transition-all duration-150
                    disabled:opacity-60 disabled:cursor-not-allowed
                    focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                >
                  {/* Accent top bar */}
                  <div className={`absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r ${accent.bar}
                    opacity-0 group-hover:opacity-100 transition-opacity duration-200`} />

                  {/* Logo / icon */}
                  {integration.logoUrl ? (
                    <div className={`w-9 h-9 rounded-lg ${accent.iconBg} flex items-center justify-center flex-shrink-0
                      border border-line-subtle dark:border-dark-600 overflow-hidden`}>
                      <img
                        src={integration.logoUrl}
                        alt={integration.name}
                        className="w-6 h-6 object-contain"
                      />
                    </div>
                  ) : (
                    <div className={`w-9 h-9 rounded-lg ${accent.iconBg} flex items-center justify-center flex-shrink-0`}>
                      <PlatformIcon type={typeLabel} className={`w-4 h-4 ${accent.iconColor}`} />
                    </div>
                  )}

                  {/* Name + type */}
                  <div className="flex-1 min-w-0">
                    <p className="text-ui font-semibold text-content dark:text-gray-100 truncate capitalize">
                      {integration.name}
                    </p>
                    <p className="text-meta font-medium text-content-muted dark:text-gray-400 capitalize truncate">
                      {typeLabel} Integration
                    </p>
                  </div>

                  {/* Type badge */}
                  <span className={`hidden sm:flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold
                    border ${accent.badge} flex-shrink-0 capitalize mr-2`}>
                    {typeLabel}
                  </span>

                  {/* CTA arrow / spinner */}
                  <div className={`flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center
                    ${accent.iconBg} ${accent.iconColor}
                    group-hover:scale-105 transition-transform duration-150`}>
                    {isThisConnecting ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <ArrowRight className="w-3.5 h-3.5" />
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* ── Footer ── */}
        <div className="px-6 pb-5 pt-3 border-t border-line dark:border-dark-700 flex-shrink-0">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[12px] font-medium text-content-subtle dark:text-gray-500">
              {isLoading ? "" : `${filtered.length} integration${filtered.length !== 1 ? "s" : ""} available`}
            </p>
            <DialogClose asChild>
              <button
                onClick={() => setOpen(false)}
                disabled={isPending}
                className="px-4 py-2 text-[13px] font-semibold
                  bg-surface dark:bg-dark-700
                  border border-line dark:border-dark-600
                  text-gray-700 dark:text-gray-300
                  rounded-xl hover:bg-gray-50 dark:hover:bg-dark-600
                  hover:border-line-strong dark:hover:border-dark-500
                  disabled:opacity-60 transition-all duration-150
                  focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              >
                Cancel
              </button>
            </DialogClose>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

